import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, switchMap, tap, catchError, of, withLatestFrom, map, retryWhen, concatMap, throwError, timer } from 'rxjs';
import { PatientService } from './patient.service';
import { SocketService } from './socket.service';
import { withPdfPages$ } from '../pdf-pages/with-pdf-pages';

@Injectable({
  providedIn: 'root'
})
export class PatientNoteService {
  private apiUrl = `api/notes`;

  private patientNotesSubject = new BehaviorSubject<any[] | null>(null);
  private patientMediaSubject = new BehaviorSubject<any[] | null>(null);
  private editingNoteSubject = new BehaviorSubject<any>(null);

  patientNotes$ = this.patientNotesSubject.asObservable();
  patientMedia$ = this.patientMediaSubject.asObservable();
  editingNote$ = this.editingNoteSubject.asObservable();

  private patientId?: string;

  constructor(
    private http: HttpClient,
    private patientService: PatientService,
    private socketService: SocketService
  ) {
    this.subscribeToCurrentPatient();
    this.setupSocketListeners();
  }

  private subscribeToCurrentPatient(): void {
    this.patientService.currentPatient$.pipe(
      switchMap(patient => {
        if (patient) {
          this.patientId = patient.id;
          // Clear data synchronously before async load starts
          this.patientNotesSubject.next([]);
          return this.loadNotesByPatient(patient.id).pipe(
            retryWhen(errors =>
              errors.pipe(
                concatMap((error, index) => {
                  // Don't retry for client errors (4xx) except 408 (timeout) and 429 (rate limit)
                  const status = error?.status;
                  const shouldRetry = !status || (status >= 500) || status === 408 || status === 429;
                  
                  if (!shouldRetry || index >= 2) {
                    // Max retries exceeded or non-retryable error
                    return throwError(() => error);
                  }
                  
                  const delayTime = 1000 * Math.pow(2, index); // Exponential backoff: 1s, 2s
                  console.warn(`Retrying patient notes load (attempt ${index + 1}/3) after ${delayTime}ms`, error);
                  return timer(delayTime);
                })
              )
            ),
            catchError((error) => {
              console.error('Error loading patient notes after retries:', error);
              // Return empty array on error to clear stale data
              return of({ data: [] });
            })
          );
        } else {
          // If there's no current patient, clear the notes
          this.patientId = undefined;
          this.patientNotesSubject.next([]);
          return of({ data: [] }); // Return an observable with empty array
        }
      }),
      tap((results: any) => {
        // Update the BehaviorSubject with the new or cleared notes
        const notes = results ? results.data : [];
        this.patientNotesSubject.next(notes);
        this.updatePatientMedia();
      }),
      catchError((error) => {
        console.error('Error in patient notes subscription:', error);
        this.patientNotesSubject.next([]);
        return of({ data: [] });
      })
    ).subscribe();
  }

  private setupSocketListeners(): void {
    // Listener for when a new note is added - use reactive patient ID tracking
    this.socketService.listen('noteAdded', 'patient-note-service').pipe(
      withLatestFrom(this.patientService.currentPatient$),
      map(([note, currentPatient]) => {
        // Only process if note belongs to current patient
        const currentPatientId = currentPatient?.id;
        if (note.patientId === currentPatientId && currentPatientId) {
          return { note, currentPatientId };
        }
        return null;
      })
    ).subscribe(result => {
      if (result) {
        const currentNotes = this.patientNotesSubject.getValue() || [];
        // Double-check patient ID matches before adding (defense against race conditions)
        if (result.note.patientId === result.currentPatientId) {
          currentNotes.unshift(result.note);
          this.patientNotesSubject.next(currentNotes);
          this.updatePatientMedia();
        }
      }
    });

    // Listener for when a note is updated - use reactive patient ID tracking
    this.socketService.listen('noteUpdated', 'patient-note-service').pipe(
      withLatestFrom(this.patientService.currentPatient$),
      map(([updatedNote, currentPatient]) => {
        // Only process if note belongs to current patient
        const currentPatientId = currentPatient?.id;
        if (updatedNote.patientId === currentPatientId && currentPatientId) {
          return { updatedNote, currentPatientId };
        }
        return null;
      })
    ).subscribe(result => {
      if (result) {
        // Double-check patient ID matches before processing (defense against race conditions)
        if (result.updatedNote.patientId !== result.currentPatientId) {
          return;
        }
        
        const currentNotes = this.patientNotesSubject.getValue() || [];
        const noteIndex = currentNotes.findIndex(note => note.id === result.updatedNote.id);
        
        if (noteIndex >= 0) {
          // Note exists, update it but preserve processingStatus from existing media
          const existingNote = currentNotes[noteIndex];
          const existingMedia = existingNote.media || [];
          const newMedia = result.updatedNote.media || [];
          
          // Build a map of PDF URLs to check if they have converted images
          const pdfUrlsWithImages = new Set<string>();
          newMedia.forEach((media: any) => {
            // If this is an image with originalPdfUrl, the PDF has been converted
            if (media.mediaType !== 'application/pdf' && media.originalPdfUrl) {
              pdfUrlsWithImages.add(media.originalPdfUrl);
            }
          });
          
          // Update media: preserve processingStatus only for PDFs that don't have converted images yet
          const updatedMedia = newMedia.map((newMediaItem: any) => {
            const existingMediaItem = existingMedia.find((m: any) => m.id === newMediaItem.id);
            
            // If this is a PDF with processingStatus
            if (newMediaItem.mediaType === 'application/pdf' && existingMediaItem?.processingStatus === 'processing') {
              // Only preserve processingStatus if this PDF doesn't have converted images yet
              if (!pdfUrlsWithImages.has(newMediaItem.mediaUrl)) {
                return { ...newMediaItem, processingStatus: 'processing' };
              }
              // PDF has converted images, remove processingStatus
              return newMediaItem;
            }
            
            // For non-PDFs or PDFs without processingStatus, use the new media as-is
            return newMediaItem;
          });
          
          const mergedNote = {
            ...result.updatedNote,
            media: updatedMedia
          };
          
          const updatedNotes = currentNotes.map(note => note.id === result.updatedNote.id ? mergedNote : note);
          this.patientNotesSubject.next(updatedNotes);
        } else if (result.updatedNote.active) {
          // Note doesn't exist but is now active (restored), add it to the list
          const updatedNotes = [result.updatedNote, ...currentNotes];
          this.patientNotesSubject.next(updatedNotes);
        }
        this.updatePatientMedia();
      }
    });

    // Listener for when a note is deleted - use reactive patient ID tracking
    this.socketService.listen('noteDeleted', 'patient-note-service').pipe(
      withLatestFrom(this.patientService.currentPatient$),
      map(([deletedNote, currentPatient]) => {
        // Only process if note belongs to current patient
        const currentPatientId = currentPatient?.id;
        if (deletedNote.patientId === currentPatientId && currentPatientId) {
          return { deletedNote, currentPatientId };
        }
        return null;
      })
    ).subscribe(result => {
      if (result) {
        // Double-check patient ID matches before processing (defense against race conditions)
        if (result.deletedNote.patientId !== result.currentPatientId) {
          return;
        }
        const currentNotes = this.patientNotesSubject.getValue() || [];
        const updatedNotes = currentNotes.filter(note => note.id !== result.deletedNote.id);
        this.patientNotesSubject.next(updatedNotes);
        this.updatePatientMedia();
      }
    });


    // Listener for when note media is deleted - use reactive patient ID tracking
    this.socketService.listen('noteMediaDeleted', 'patient-note-service').pipe(
      withLatestFrom(this.patientService.currentPatient$),
      map(([deletedMedia, currentPatient]) => {
        // Only process if media belongs to current patient
        const currentPatientId = currentPatient?.id;
        if (deletedMedia.patientId === currentPatientId && currentPatientId) {
          return { deletedMedia, currentPatientId };
        }
        return null;
      })
    ).subscribe(result => {
      if (result) {
        // Double-check patient ID matches before processing (defense against race conditions)
        if (result.deletedMedia.patientId !== result.currentPatientId) {
          return;
        }
        
        const currentNotes = this.patientNotesSubject.getValue() || [];
        const index = currentNotes.findIndex((n: any) => n.id === result.deletedMedia.patientNoteId);

        if (index >= 0 && currentNotes[index]) {
          const media = currentNotes[index].media as any[];
          const mediaIndex = media.findIndex(m => m.id === result.deletedMedia.id);

          if (mediaIndex >= 0) {
            media.splice(mediaIndex, 1);
            currentNotes[index].media = media;
            this.patientNotesSubject.next(currentNotes);
            this.updatePatientMedia();
          }
        }
      }
    });

    // Listener for when note media is annotated - use reactive patient ID tracking
    this.socketService.listen('noteMediaAnnotated', 'patient-note-service').pipe(
      withLatestFrom(this.patientService.currentPatient$),
      map(([annotatedMedia, currentPatient]) => {
        // Only process if media belongs to current patient
        const currentPatientId = currentPatient?.id;
        if (annotatedMedia.patientId === currentPatientId && currentPatientId) {
          return { annotatedMedia, currentPatientId };
        }
        return null;
      })
    ).subscribe(result => {
      if (result) {
        // Double-check patient ID matches before processing (defense against race conditions)
        if (result.annotatedMedia.patientId !== result.currentPatientId) {
          return;
        }
        
        const currentNotes = this.patientNotesSubject.getValue() || [];
        const noteIndex = currentNotes.findIndex(note => note.id === result.annotatedMedia.patientNoteId);
        if (noteIndex < 0) return;

        const mediaList = currentNotes[noteIndex].media as any[];

        // Try to find by originalMediaId, fall back to id match
        let originalIndex = mediaList.findIndex(m => m.id === result.annotatedMedia.originalMediaId);
        if (originalIndex < 0) {
          originalIndex = mediaList.findIndex(m => m.id === result.annotatedMedia.id);
        }

        if (originalIndex >= 0) {
          const originalMedia = mediaList[originalIndex];

          // Ensure it's a proper array
          if (!originalMedia.annotatedVersions) {
            originalMedia.annotatedVersions = [];
          }

          // Insert new annotation
          originalMedia.annotatedVersions.unshift(result.annotatedMedia);
          this.patientNotesSubject.next(currentNotes); // Refresh local state
        }

        // Always update flattened patient media array
        this.updatePatientMedia();
      }
    });

    // Listener for when PDF conversion starts - use reactive patient ID tracking
    this.socketService.listen('pdfConversionStarted', 'patient-note-service').pipe(
      withLatestFrom(this.patientService.currentPatient$),
      map(([data, currentPatient]) => {
        // Only process if data belongs to current patient
        const currentPatientId = currentPatient?.id;
        if (data.patientId === currentPatientId && currentPatientId && data.mediaId) {
          return { data, currentPatientId };
        }
        return null;
      })
    ).subscribe(result => {
      if (result) {
        // Double-check patient ID matches before processing (defense against race conditions)
        if (result.data.patientId !== result.currentPatientId) {
          return;
        }
        
        const currentNotes = this.patientNotesSubject.getValue() || [];
        const noteIndex = currentNotes.findIndex(note => note.id === result.data.patientNoteId);
        
        if (noteIndex >= 0) {
          const mediaList = currentNotes[noteIndex].media as any[];
          const mediaIndex = mediaList.findIndex(m => m.id === result.data.mediaId);
          
          if (mediaIndex >= 0) {
            // Mark PDF as processing
            mediaList[mediaIndex] = {
              ...mediaList[mediaIndex],
              processingStatus: 'processing'
            };
            currentNotes[noteIndex] = {
              ...currentNotes[noteIndex],
              media: mediaList
            };
            this.patientNotesSubject.next([...currentNotes]);
          }
        }
      }
    });

    // Listener for when PDF conversion completes
    // Note: We don't update processingStatus here - let noteUpdated handle it
    // This ensures we only remove processingStatus for the specific PDF that completed
    // when we see the actual converted images in the noteUpdated event
    this.socketService.listen('pdfConversionCompleted', 'patient-note-service').subscribe((data: any) => {
      // This event is mainly for tracking/logging purposes
      // The noteUpdated event will handle the actual status updates
    });

  }

  private updatePatientMedia() {
    const notes = this.patientNotesSubject.getValue();
    // Extract all media from the notes into a single array
    const mediaArray = notes?.reduce((acc, note) => {
      if (note.media && Array.isArray(note.media)) {
        return acc.concat(note.media);
      }
      return acc;
    }, []);

    this.patientMediaSubject.next([...mediaArray]);
  }

  loadNotesByPatient(patientId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/patient/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  addNote(noteData: any): Observable<any> {
    const token = localStorage.getItem('token');
    return withPdfPages$(noteData).pipe(switchMap((body) => this.http.post<any>(this.apiUrl, body, {
      headers: { Authorization: `Bearer ${token}` }
    })));
  }

  getNoteById(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.apiUrl}/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updateNote(id: string, noteData: any): Observable<any> {
    const token = localStorage.getItem('token');
    return withPdfPages$(noteData).pipe(switchMap((body) => this.http.put<any>(`${this.apiUrl}/${id}`, body, {
      headers: { Authorization: `Bearer ${token}` }
    })));
  }

  deleteNote(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.delete(`${this.apiUrl}/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  deleteNoteMedia(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.delete(`${this.apiUrl}/media/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  uploadAnnotatedMedia(blob: Blob, originalMedia: any): Observable<any> {
    /*  console.log('[Uploading Annotated Image]', {
       blobSize: blob.size,
       mediaId: originalMedia.id,
       noteId: originalMedia.patientNoteId,
       patientId: originalMedia.patientId,
     }); */
    const token = localStorage.getItem('token');
    const fileName = `annotated-${Date.now()}.png`;
    const file = new File([blob], fileName, { type: 'image/png' });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('originalMediaId', originalMedia.id);
    formData.append('patientId', originalMedia.patientId);
    formData.append('patientNoteId', originalMedia.patientNoteId);
    formData.append('fileName', fileName);

    return this.http.post<any>(`${this.apiUrl}/media/annotated`, formData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }


  seedSamplePatientNotes(count: number, patientId: string) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/seed/${count}`, { patientId }, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      switchMap(res => {
        return this.loadNotesByPatient(patientId);
      })
    );
  }

  setEditingNote(note: any | null) {
    this.editingNoteSubject.next(note);
  }

  loadDeletedTimelineItems(patientId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.apiUrl}/deleted/patient/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  restoreTimelineItem(id: string, type: 'note' | 'admission-record'): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put<any>(`${this.apiUrl}/restore/${id}`, { type }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

}
